// @flow

import { AdvanceSearchTab, StandardSearchTab } from 'src/constants/search';

export const routes = {
  // Common
  oauth: {
    view: () => '/oauth'
  },
  betaFeatures: {
    view: () => '/beta-features'
  },
  // Landing page
  landingPage: {
    view: () => `/landing-page/new-features`
  },

  // onboarding new feature for old user before release day 042023
  onboardingOldUser: {
    view: () => `/onboarding-introduce-feature`
  },
  // Setup
  finalize: {
    view: () => '/oauth/finalize'
  },
  onboarding: {
    view: () => '/onboarding'
  },
  kyc: {
    view: () => '/kyc'
  },
  demographic: {
    view: () => '/demographic'
  },
  chooseLayout: {
    view: () => '/choose-layout'
  },
  initializing: {
    view: () => '/initializing'
  },
  setupComplete: {
    view: () => '/setup-complete'
  },
  verifySetup: {
    view: () => '/verify-setup'
  },
  newInstallation: {
    running: () => '/installation/running',
    onboarding: () => '/installation/onboarding',
    successInstall: () => '/installation/success-install',
    kyc: () => '/installation/kyc',
    chooseTheme: () => '/installation/choose-theme',
    survey: () => '/installation/survey',
    selectFeature: () => '/installation/select-feature',
    selectThemeNewSimplify: () => '/installation/select-theme-simplify',
    selectThemeNewSimplifyRunning: () => '/installation/select-theme-simplify-running',
    selectTemplate: () => '/installation/select-template-design',
    enableBoostCore: () => '/installation/enable-boost-core',
    complete: () => '/installation/completion',
    onboardingPlan: () => '/installation/onboarding-plan',
    setupTheme: themeId => `/installation/shopify-integration/${themeId}/new`,
    viewSetupTheme: themeId => `/installation/shopify-integration/${themeId}`
  },

  // Plan
  subscription: {
    plan: () => '/plan',
    addon: () => '/addon',
    package: () => '/package',
    upgradePackage: () => '/upgrade-package',
    packageLarge: (planNumber: string) => `/plan-large/${planNumber}`,
    discount: () => '/plan-discount',
    discountLarge: (planId: string) => `/plan-discount-large/${planId}`,
    planOffer: (planId: string) => `/plan-offer/${planId}`,
    complete: () => '/plan-complete',
    error: () => '/plan-error',
    migration: () => '/plan-migration',
    migrationComplete: () => '/plan-migration-complete',
    charge: () => '/charge',
    comparison: products => `/package-comparison/${products}`,
    comparisonV5: () => `/package-comparison`,
    gmvBillingHistory: () => '/gmv-billing-history'
  },
  chargeCustomization: {
    view: () => '/charge-customization',
    complete: () => '/charge-customization-complete',
    error: () => '/charge-customization-error'
  },

  // Home
  dashboard: {
    view: () => '/',
    viewV3: () => '/dashboard-v3'
  },

  // Sync
  sync: {
    view: () => '/sync',
    settings: () => '/sync-settings'
  },

  // Filter
  filterTree: {
    new: () => '/filter-tree/new',
    view: (filterTreeId: string) => `/filter-tree/${filterTreeId}`,
    list: () => '/filter-tree'
  },
  filterSettings: {
    view: () => '/filter-settings'
  },
  filterLayout: {
    view: () => '/filter-layout'
  },

  // Theme
  themeSetup: {
    view: () => '/theme-setup'
  },
  supportedThemeSetupSuccess: {
    view: () => '/supported-theme-setup-success'
  },
  unsupportedThemeSetupSuccess: {
    view: () => '/unsupported-theme-setup-success'
  },
  themeRevert: {
    view: () => '/theme-revert'
  },
  themeRevertSuccess: {
    view: () => '/theme-revert-success'
  },
  switchLayout: {
    view: () => '/switch-layout'
  },
  switchLayoutSuccess: {
    view: () => '/switch-layout-success'
  },
  defaultLayoutSettings: {
    view: () => '/quick-view-cart-settings'
  },
  quickViewCartSettings: {
    view: () => '/quick-view-cart-settings'
  },
  defaultThemeSettings: {
    view: () => '/default-theme-settings'
  },
  themeUpgrade: {
    view: () => '/theme-upgrade',
    success: () => '/theme-upgrade-success',
    verify: () => '/verify-upgrade'
  },
  themeSetting: {
    visualEditor: (themeId: number) => `/theme-setting/${themeId}/visual-editor`,
    editVisualDesign: themeId => `/theme-setting/${themeId}/visual-editor`
  },
  // Theme Integration (SSR)
  shopifyIntegration: {
    view: () => '/shopify-integration',
    viewThemeWidget: themeId => `/shopify-integration/${themeId}`,
    newThemeWidget: themeId => `/shopify-integration/${themeId}/new`,
    previewBoostTemplate: themeNameLib =>
      `/shopify-integration/preview-boost-template/${themeNameLib}`,
    guide: (themeId: number, templateId: string, feature: string = '') =>
      `/shopify-integration/guide/${themeId}/${templateId}/${feature}`,

    codeEditor: (templateId: string) => `/shopify-integration/code-editor/${templateId}`,
    manageTemplateInstance: (themeId: number) =>
      `/shopify-integration-template-instances/${themeId}`,
    templateLibrary: () => '/shopify-integration-template-library',
    viewShopifyIntegrationSimplify: themeId => `/shopify-integration-simplify/${themeId}`,
    shopifyIntegrationNewSimplify: themeId => `/shopify-integration-simplify/${themeId}/new`,
    upgradeToTurbo: (themeId: number) => `/shopify-integration/${themeId}/upgrade-to-turbo`
  },
  shopifyIntegrationSettings: {
    visualEditor: (templateId: string) =>
      `/shopify-integration-settings/visual-editor/${templateId}`
  },

  // Tool
  translation: {
    view: () => '/translation',
    feature: (feature) => `/translation/${feature}`,
  },
  mergeValues: {
    view: () => '/merge-values',
    new: () => '/merge-values/new',
    edit: (type: string) => `/merge-values/${type}`
  },
  integration: {
    view: () => '/integration',
    thirdParty: {
      view: () => '/third-party'
    },
    klaviyo: {
      view: () => '/klaviyo-integration',
      callback: () => '/klaviyo-integration/callback',
      callbackSuccess: () => '/klaviyo-integration/callback-success'
    },
    reviewsio: {
      view: () => '/reviewsio-integration'
    },
    attentive: {
      view: () => '/attentive-integration',
      callback: () => '/attentive-integration/callback',
      callbackSuccess: () => '/attentive-integration/callback-success'
    },
    globalE: {
      view: () => '/global-selling'
    },
    gorgias: {
      view: () => '/gorgias-integration',
    },
    shopifyCombinedListings: {
      view: () => '/shopify-combined-listings-integration',
    },
    'google-analytics-4': {
      view: () => '/google-analytics-4-integration',
      callback: () => '/google-analytics-4-integration/callback',
      callbackSuccess: () => '/google-analytics-4-integration/callback-success'
    }
  },
  productVisibility: {
    view: () => '/product-visibility'
  },
  metafield: {
    view: () => '/metafield'
  },
  customRangeSlider: {
    view: () => '/custom-range-slider'
  },
  swatchSettings: {
    view: () => '/swatch-settings'
  },
  customSorting: {
    view: () => '/custom-sorting'
  },
  variantsDisplay: {
    view: () => '/variants-display'
  },
  languageSetting: {
    view: () => '/language-setting'
  },
  account: {
    view: () => '/account'
  },
  appPreferences: {
    view: () => '/preferences'
  },
  generalSwatch: {
    view: () => '/general-swatch',
    settings: (filterName: string) => `/general-swatch/settings/${filterName}`
  },

  // Search unused
  instantSearchDisplay: {
    view: () => '/search/instant-search-display'
  },
  searchSettings: {
    view: () => '/search/search-settings'
  },
  searchPageDisplay: {
    view: () => '/search/search-page-display'
  },
  // end unused

  // Standard search
  stopWords: {
    view: () => `/search/standard-search/${StandardSearchTab.stopWords}`
  },
  redirect: {
    // view: () => `/search/standard-search/#${StandardSearchTab.redirects}`,
    // new: () => `/search/standard-search/#${StandardSearchTab.newRedirects}`,
    // edit: () => `/search/standard-search/#${StandardSearchTab.editRedirects}`
    view: () => `/search/standard-search/${StandardSearchTab.redirects}`,
    new: () => `/search/standard-search/${StandardSearchTab.newRedirects}`,
    edit: () => `/search/standard-search/${StandardSearchTab.editRedirects}`
  },
  suggestionDictionary: {
    view: () => `/search/standard-search/${StandardSearchTab.suggestionDictionary}`,
    new: () => `/search/standard-search/${StandardSearchTab.newSuggestionDictionary}`,
    edit: () => `/search/standard-search/${StandardSearchTab.editSuggestionDictionary}`
  },
  synonym: {
    view: () => `/search/standard-search/${StandardSearchTab.manualSynonyms}`,
    new: () => `/search/standard-search/${StandardSearchTab.newManualSynonyms}`,
    edit: () => `/search/standard-search/${StandardSearchTab.editManualSynonyms}`
  },
  instantSearchWidget: {
    view: () => `/search/standard-search/${StandardSearchTab.instantSearchWidget}`
  },
  searchControl: {
    view: () => `/search/search-control`
  },
  relevanceSettings: {
    view: () => `/search/standard-search/${StandardSearchTab.relevanceSettings}`
  },
  searchResultsPage: {
    view: () => `/search/standard-search/${StandardSearchTab.searchResultsPage}`
  },
  inCollectionSearch: {
    view: () => `/search/standard-search/${StandardSearchTab.inCollection}`
  },

  // Advanced search
  generalAdvancedSearch: {
    view: () => `/search/advanced-search/${AdvanceSearchTab.generalSearch}`
  },
  aiSynonym: {
    view: () => `/search/advanced-search/${AdvanceSearchTab.aiSynonyms}`
  },
  semanticSearch: {
    view: () => `/search/advanced-search/${AdvanceSearchTab.semanticSearch}`
  },
  testYourSearch: {
    view: () => `/search/test-your-search`
  },
  productPerformanceRanking: {
    view: () => `/search/advanced-search/${AdvanceSearchTab.productPerformanceRanking}`
  },
  searchPersonalization: {
    view: () => `/search/advanced-search/${AdvanceSearchTab.searchPersonalization}`
  },
  // Personalization
  personalization: {
    view: () => `/personalization`
  },
  // Merchandising
  productRanking: {
    view: () => '/product-ranking'
  },
  merchandising: {
    view: () => '/merchandising',
    search: () => '/merchandising/search',
    collection: () => '/merchandising/collection',
    new: (ruleType: string) => `/merchandising/new/${ruleType}`,
    edit: (ruleId: string) => `/merchandising/${ruleId}`
  },
  analytics: {
    view: () => '/analytics',
    // For analytics v2
    // Search
    overviewSearch: () => '/search-analytics',
    searches: () => '/search-analytics/searches',
    searchTermDetail: (searchTerm: string) => `/search-analytics/searches/${searchTerm}`,
    searchesWithClick: () => '/search-analytics/searches-with-click',
    searchTermWithClickDetail: (searchTerm: string) =>
      `/search-analytics/searches-with-click/${searchTerm}`,
    searchesWithoutClicks: () => '/search-analytics/searches-without-clicks',
    searchesWithoutResults: () => '/search-analytics/searches-without-results',
    productPerformance: () => '/search-analytics/product-performance',

    //App impact
    appImpact: () => '/app-impact',

    // Collection
    overviewCollection: () => '/collection-analytics',
    collectionPerformance: () => '/collection-analytics/collection-performance',
    filterOptionValue: () => '/collection-analytics/filter-option-value',
    filterOptionValueCombination: () => '/collection-analytics/filter-option-value-combination',

    // Recommendation
    recommendationOverview: () => '/recommendation-analytics',
    recommendationPagePerformance: () => '/recommendation-analytics/page-performance',
    recommendationWidgetPerformance: () => '/recommendation-analytics/widget-performance',
    recommendationProductPerformance: () => '/recommendation-analytics/product-performance',

    // Pre-Order
    preOrderProduct: () => '/pre-order-analytics/products',
    preOrders: () => '/pre-order-analytics/pre-orders',

    // Settings
    settings: () => '/analytic-settings'
  },
  appPermissionSuccess: {
    view: () => '/update-permission-success',
    analyticsThemeV1: () => '/update-permission-success-theme-v1-analytics'
  },

  // Recommendation
  recommendationGeneralSettings: {
    settings: () => '/recommendation-settings'
  },
  recommendationWidgets: {
    list: (tab: number) => (tab ? `/recommendation-widgets?tab=${tab}` : '/recommendation-widgets'),
    new: () => '/recommendation-widgets/new',
    newPersonalized: () => '/recommendation-widgets/new-personalized',
    view: (widgetId: string) => `/recommendation-widgets/${widgetId}`,
    guide: () => '/recommendation-widgets-steps-guide',
    guideCartDrawer: () => '/recommendation-widgets-guide-cart-drawer'
  },
  manualRecommendation: {
    list: () => '/manual-recommendations',
    new: () => '/manual-recommendations/new',
    view: (productId: string) => `/manual-recommendations/${productId}`
  },
  predictiveBundle: {
    list: () => '/predictive-bundle',
    new: () => '/predictive-bundle/new',
    view: (widgetId: string) => `/predictive-bundle/${widgetId}`
  },
  recommendationDefaultDesignSettings: {
    view: () => '/recommendation-default-design-settings'
  },
  standardSearch: {
    view: hash => `/search/standard-search${hash ? `/#${hash}` : ''}`
  },
  advancedSearch: {
    view: hash => `/search/advanced-search${hash ? `/#${hash}` : ''}`
  },
  aiPowered: {
    view: () => `/ai-powered-landing`
  },
  landing: {
    turbo: () => `/turbo-landing`
  },
  settings: {
    view: () => '/settings'
  },
  globalSelling: {
    view: () => '/global-selling'
  },
  wholesaleSelling: {
    view: () => '/wholesale-selling'
  },
  backInStock: {
    settings: () => '/back-in-stock-settings',
    guide: () => '/back-in-stock-settings/guide',
    subscription: () => '/back-in-stock-subscription',
    new: () => '/back-in-stock-subscription/new'
  },
  preOrder: {
    list: () => '/pre-order',
    new: () => '/pre-order/new',
    notifySetting: () => '/pre-order/notification-settings',
    view: (id: string) => `/pre-order/${id}`
  },
  tools: {
    domainSetting: () => '/tools/domain-settings'
  },
  abTesting: {
    list: () => '/ab-testing',
    new: type => `/ab-testing/${type}/new`,
    view: id => `/ab-testing/${id}/view`,
    analytics: id => `/ab-testing/${id}/analytics`
  },
  countdown: {
    list: () => '/countdown',
    new: () => '/countdown/new',
    view: id => `/countdown/${id}`
  },
  postPurchase: {
    list: () => '/post-purchase',
    edit: id => `/post-purchase/${id}`,
    new: () => `/post-purchase/new`
  }
};
